import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {Id, View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    Caption,
    HeadingSmall,
    LabelMedium,
    LabelSmall,
} from "@khanacademy/wonder-blocks-typography";

import withApollo, {
    type WithApolloProps,
    type WithoutApollo,
} from "#data-access/gql/with-apollo.tsx";
import {saveAndUpdateProfile} from "#data-access/user-profile-provider.tsx";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import BioPicker from "#profile-settings-package/bio-picker.tsx";
import type {
    ProfileAccessLevel,
    ProfileModelType,
} from "#shared-package/profile-model.ts";

type OwnProps = {
    successfullyChangedSettings: () => unknown;
};

type Props = OwnProps & WithUserProfileProps & WithApolloProps;

type State = {
    accessLevel: ProfileAccessLevel | null | undefined;
    bioValue: string;
    bioError: string | null | undefined;
    isLoading: boolean;
};

// This component is based on ProfileInfoEditFields. This component allows users
// to edit their bio on the settings page with an auto-save mechanism.

class PublicProfileInfoSection extends React.Component<Props, State> {
    state: State = {
        accessLevel: null,
        bioValue: "",
        bioError: null,
        isLoading: false,
    };

    componentDidMount() {
        this._isMounted = true;

        if (this.props.profile) {
            this.initialize(this.props.profile);
        }
    }

    componentDidUpdate(oldProps: Props) {
        const newProps = this.props;
        if (!oldProps.profile && newProps.profile) {
            this.initialize(newProps.profile);
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _isMounted = false;

    initialize(profile: ProfileModelType) {
        // NOTE(jvoll): Pulling this out into a variable helps TypeScript verify that
        // it isn't null in the second part of the ternary below.
        const userProfile = profile.get("profile");
        this.setState({
            accessLevel: userProfile?.accessLevel ?? "COACH",
            bioValue: profile.get("bio"),
        });
    }

    savePublicProfile: () => void = () => {
        const {successfullyChangedSettings, gqlClient} = this.props;
        const {bioValue, accessLevel} = this.state;

        this.setState({isLoading: true});

        saveAndUpdateProfile(gqlClient, {
            bio: bioValue,
            ...(accessLevel ? {profile: {accessLevel}} : null),
        })
            .then(() => {
                if (!this._isMounted) {
                    return;
                }
                successfullyChangedSettings();
                this.setState({isLoading: false});
            })
            .catch(() => {
                if (!this._isMounted) {
                    return;
                }
                this.setState({
                    bioError: t`Sorry, we had trouble saving. Please try again later.`,
                    isLoading: false,
                });
            });
    };

    render(): React.ReactNode {
        const {profile} = this.props;
        const {accessLevel, bioValue, bioError, isLoading} = this.state;
        return (
            <View role="region" aria-label={t`Public profile settings`}>
                <HeadingSmall style={styles.header} tag="h2">
                    {t`Public profile information`}
                </HeadingSmall>
                <Strut size={spacing.medium_16} />
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- TODO: Address a11y error */}
                <form
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                        }
                    }}
                    onKeyUp={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            this.savePublicProfile();
                        }
                    }}
                >
                    <View>
                        <Id>
                            {(bioPickerId) => (
                                <>
                                    <LabelMedium
                                        htmlFor={bioPickerId}
                                        style={styles.label}
                                        tag="label"
                                    >
                                        {t`Bio`}
                                    </LabelMedium>
                                    {profile && (
                                        <BioPicker
                                            id={bioPickerId}
                                            onChange={(bioValue) => {
                                                this.setState({bioValue});
                                            }}
                                            value={bioValue}
                                        />
                                    )}
                                </>
                            )}
                        </Id>
                        <div aria-live="polite">
                            {bioError != null && (
                                <Caption style={styles.errorMessage}>
                                    {bioError}
                                </Caption>
                            )}
                        </div>
                    </View>
                </form>
                <Strut size={spacing.medium_16} />
                <View>
                    <LabelMedium
                        style={styles.label}
                        htmlFor="accessLevel"
                        tag="label"
                    >
                        {t`Visibility`}
                    </LabelMedium>
                    <LabelSmall style={styles.description}>
                        {t`Who can see the Showcase and Badge Counts sections on your profile?`}
                    </LabelSmall>
                    <Strut size={spacing.medium_16} />

                    <SingleSelect
                        id="accessLevel"
                        placeholder=""
                        onChange={(selectedAccessLevel) => {
                            switch (selectedAccessLevel) {
                                case "COACH":
                                case "PUBLIC":
                                    this.setState({
                                        accessLevel: selectedAccessLevel,
                                    });
                                    break;
                                default:
                                    this.setState({
                                        accessLevel: "COACH",
                                    });
                            }
                        }}
                        selectedValue={accessLevel}
                    >
                        <OptionItem label={t`Coaches`} value="COACH" />
                        <OptionItem label={t`Anyone`} value="PUBLIC" />
                    </SingleSelect>
                </View>
                <Strut size={spacing.large_24} />
                <Button
                    onClick={this.savePublicProfile}
                    style={styles.button}
                    spinner={isLoading}
                >
                    {t`Save changes`}
                </Button>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    header: {
        color: color.offBlack,
    },
    label: {
        color: color.offBlack,
        marginBottom: spacing.xSmall_8,
    },
    errorMessage: {
        color: color.red,
        marginTop: spacing.xxxSmall_4,
    },
    button: {
        alignSelf: "flex-start",
        paddingLeft: spacing.medium_16,
        paddingRight: spacing.medium_16,
    },
    description: {
        color: color.offBlack64,
        margin: 0,
    },
});

type ExportProps = WithoutApollo<
    WithoutUserProfile<
        JSX.LibraryManagedAttributes<
            typeof PublicProfileInfoSection,
            React.ComponentProps<typeof PublicProfileInfoSection>
        >
    >
>;

export default compose(
    withUserProfile,
    withApollo,
)(PublicProfileInfoSection) as React.ComponentType<ExportProps>;
